/**
 * PATH: packages/email-hub/src/feature/reader-header/runtime/useReaderActionRunner.ts
 * FILE_ID: EMAILHUB-READERHEADER-RUNTIME-RUNNER-001
 * PURPOSE: Execute any ReaderActionDescriptor with the plan's full feedback
 *   contract: same-frame optimistic apply, 400ms deferred spinner, 3s
 *   "still working", 10s abort+Retry, 8s undo, classified error toasts,
 *   per-busyKey idempotency, and a one-shot reconcile budget.
 * ROLE IN ARCHITECTURE: The single action-semantics engine under
 *   useReaderHeaderCommands and DecisionActions (plan Phase 2).
 * MAIN EXPORTS: useReaderActionRunner, ReaderRunSpec, ReaderActionPhase.
 * NON-RESPONSIBILITIES: No API code — transports come from proven hooks.
 * NOTES FOR FUTURE AI: All timings come from tokens.ACTION_TIMING_MS. Retries
 *   REUSE the original actionId (idempotent intent key). Never toast raw
 *   error text — descriptor `errors` mappings own the copy.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { runWithActionId } from '@quotech/api'

import { useToast } from '../../../_internal/components/Toast'
import type {
  ReaderActionDescriptor,
  ReaderHeaderSurface,
  ReaderInputMethod,
} from '../model/types'
import { ACTION_TIMING_MS } from '../tokens'
import { classifyReaderError } from './errorClassification'
import { cardinalityBucket, emitReaderTelemetry } from './telemetry'
import { captureFocus, focusReaderCommandRegion } from './focusManager'

export type ReaderActionPhase = 'idle' | 'pending' | 'spinner' | 'still-working'

export interface ReaderRunContext {
  /** Correlation id — rides as X-Correlation-ID via the observability seam. */
  actionId: string
  /** Aborted at ACTION_TIMING_MS.timeoutAt; pass to transports that accept it. */
  signal: AbortSignal
}

export interface ReaderRunSpec {
  action: ReaderActionDescriptor
  inputMethod?: ReaderInputMethod
  /** Server transport (existing mutation hooks). Never duplicates API code. */
  execute: (ctx: ReaderRunContext) => Promise<unknown>
  /** Same-frame local paint + its exact rollback. */
  optimistic?: {
    apply: () => void
    rollback: () => void
  }
  /**
   * Terminal actions (done/archive/junk/delete): remove the row + advance
   * NOW; the returned closure reinserts the row at its original position and
   * restores selection (called on server failure).
   */
  terminal?: {
    remove: () => (() => void) | void
  }
  /** Reversible actions: inverse transport held by the 8s undo toast. */
  undo?: {
    execute: (ctx: ReaderRunContext) => Promise<unknown>
    /** Local re-paint after the inverse lands (e.g. reinsert the row). */
    onRestored?: () => void
  }
  successToast?: string
  /** Target count for telemetry bucketing (default 1). */
  cardinality?: number
}

export interface ReaderActionRunner {
  /** Fire one action. Resolves when the action settles (or is suppressed). */
  run: (spec: ReaderRunSpec) => Promise<void>
  /** Synchronous local verb (reply/forward): ack + telemetry, no spinner. */
  runLocal: (
    action: ReaderActionDescriptor,
    fn: () => void,
    inputMethod?: ReaderInputMethod,
  ) => void
  phaseFor: (busyKey: string) => ReaderActionPhase
  isBusy: (busyKey: string) => boolean
}

export interface ReaderActionRunnerOptions {
  surface: ReaderHeaderSurface
  /**
   * Refetch economy: the ONE reconciliation hook (detail/list/stats patch
   * owner). Called at most once per settled action.
   */
  reconcile?: () => void
}

function newActionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `act-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

interface FlightTimers {
  spinner: ReturnType<typeof setTimeout>
  still: ReturnType<typeof setTimeout>
  timeout: ReturnType<typeof setTimeout>
}

export function useReaderActionRunner(
  options: ReaderActionRunnerOptions,
): ReaderActionRunner {
  const { surface, reconcile } = options
  const { toast } = useToast()
  const [phases, setPhases] = useState<Record<string, ReaderActionPhase>>({})
  const inFlight = useRef(new Set<string>())
  const timers = useRef(new Map<string, FlightTimers>())
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    const pending = timers.current
    return () => {
      mounted.current = false
      for (const flight of pending.values()) {
        clearTimeout(flight.spinner)
        clearTimeout(flight.still)
        clearTimeout(flight.timeout)
      }
      pending.clear()
    }
  }, [])

  const setPhase = useCallback((busyKey: string, phase: ReaderActionPhase) => {
    if (!mounted.current) return
    setPhases((prev) =>
      prev[busyKey] === phase ? prev : { ...prev, [busyKey]: phase },
    )
  }, [])

  const clearFlightTimers = useCallback((busyKey: string) => {
    const flight = timers.current.get(busyKey)
    if (!flight) return
    clearTimeout(flight.spinner)
    clearTimeout(flight.still)
    clearTimeout(flight.timeout)
    timers.current.delete(busyKey)
  }, [])

  const executeAttempt = useCallback(
    async (spec: ReaderRunSpec, actionId: string): Promise<void> => {
      const { action } = spec
      const busyKey = action.busyKey
      const inputMethod = spec.inputMethod ?? 'pointer'
      const bucket = cardinalityBucket(spec.cardinality ?? 1)
      const base = {
        actionId,
        actionKind: action.id,
        zone: action.zone,
        surface,
        inputMethod,
        cardinalityBucket: bucket,
      } as const

      inFlight.current.add(busyKey)
      emitReaderTelemetry({ ...base, event: 'reader_header.action_invoked' })

      // ── Optimistic paint: SAME frame as the press (<=100ms ack budget).
      const priorFocus = captureFocus()
      let terminalRestore: (() => void) | void = undefined
      let applied = false
      try {
        spec.optimistic?.apply()
        terminalRestore = spec.terminal?.remove()
        applied = Boolean(spec.optimistic) || Boolean(spec.terminal)
        if (applied) {
          emitReaderTelemetry({
            ...base,
            event: 'reader_header.optimistic_applied',
          })
        }
        if (spec.terminal) {
          // Terminal advance moves the keyboard to the NEW email's commands.
          focusReaderCommandRegion()
        }
      } catch {
        // A broken local reducer must not block the server action.
      }

      const controller = new AbortController()
      setPhase(busyKey, 'pending')
      let timedOut = false
      timers.current.set(busyKey, {
        spinner: setTimeout(
          () => setPhase(busyKey, 'spinner'),
          ACTION_TIMING_MS.spinnerDelay,
        ),
        still: setTimeout(
          () => setPhase(busyKey, 'still-working'),
          ACTION_TIMING_MS.stillWorkingAt,
        ),
        timeout: setTimeout(() => {
          timedOut = true
          controller.abort()
        }, ACTION_TIMING_MS.timeoutAt),
      })

      const startedAt = Date.now()
      try {
        await runWithActionId(actionId, () =>
          spec.execute({ actionId, signal: controller.signal }),
        )
        clearFlightTimers(busyKey)
        inFlight.current.delete(busyKey)
        setPhase(busyKey, 'idle')
        emitReaderTelemetry({
          ...base,
          event: 'reader_header.server_ok',
          durationMs: Date.now() - startedAt,
        })
        reconcile?.()
        if (spec.undo) {
          const undoSpec = spec.undo
          const restoreRow =
            typeof terminalRestore === 'function' ? terminalRestore : undefined
          toast(spec.successToast ?? `${action.label} — done`, 'success', {
            durationMs: ACTION_TIMING_MS.undoWindow,
            action: {
              label: 'Undo',
              onClick: () => {
                emitReaderTelemetry({ ...base, event: 'reader_header.rollback' })
                void runWithActionId(actionId, () =>
                  undoSpec.execute({ actionId, signal: new AbortController().signal }),
                )
                  .then(() => {
                    // Undo restores prior selection focus + the removed row.
                    restoreRow?.()
                    undoSpec.onRestored?.()
                    priorFocus.restore()
                    reconcile?.()
                  })
                  .catch((undoError: unknown) => {
                    toast(action.errors[classifyReaderError(undoError)], 'error')
                  })
              },
            },
          })
        } else if (spec.successToast) {
          toast(spec.successToast, 'success')
        }
      } catch (error) {
        clearFlightTimers(busyKey)
        inFlight.current.delete(busyKey)
        setPhase(busyKey, 'idle')
        const errorClass = timedOut ? 'network' : classifyReaderError(error)
        emitReaderTelemetry({
          ...base,
          event: 'reader_header.server_error',
          durationMs: Date.now() - startedAt,
          errorClass,
        })
        // ── Rollback: reinsert the row at its original position, restore
        // selection + focus (kills the silent Archive/Delete failure).
        if (applied) {
          try {
            spec.optimistic?.rollback()
            if (typeof terminalRestore === 'function') terminalRestore()
            priorFocus.restore()
          } catch {
            // Rollback is best-effort; reconcile below repairs the view.
          }
          emitReaderTelemetry({ ...base, event: 'reader_header.rollback' })
        }
        reconcile?.()
        const retryable = errorClass === 'network'
        toast(
          timedOut
            ? `${action.label} is taking too long — request cancelled.`
            : action.errors[errorClass],
          'error',
          retryable
            ? {
                action: {
                  label: 'Retry',
                  // Retry REUSES the same actionId (idempotent intent key).
                  onClick: () => void executeAttempt(spec, actionId),
                },
              }
            : undefined,
        )
      }
    },
    [clearFlightTimers, reconcile, setPhase, surface, toast],
  )

  const run = useCallback(
    async (spec: ReaderRunSpec): Promise<void> => {
      // Idempotency: an in-flight busyKey swallows duplicate submits.
      if (inFlight.current.has(spec.action.busyKey)) return
      await executeAttempt(spec, newActionId())
    },
    [executeAttempt],
  )

  const runLocal = useCallback(
    (
      action: ReaderActionDescriptor,
      fn: () => void,
      inputMethod: ReaderInputMethod = 'pointer',
    ): void => {
      emitReaderTelemetry({
        event: 'reader_header.action_invoked',
        actionId: newActionId(),
        actionKind: action.id,
        zone: action.zone,
        surface,
        inputMethod,
        cardinalityBucket: '1',
      })
      fn()
    },
    [surface],
  )

  const phaseFor = useCallback(
    (busyKey: string): ReaderActionPhase => phases[busyKey] ?? 'idle',
    [phases],
  )
  const isBusy = useCallback(
    (busyKey: string) => inFlight.current.has(busyKey),
    [],
  )

  return { run, runLocal, phaseFor, isBusy }
}
